import React from "react";
import { View, ScrollView, Image, TouchableOpacity, Text, StyleSheet } from "react-native";
import { Ionicons } from "@expo/vector-icons";

const DEFAULT_THUMB_SIZE = 76;
const DEFAULT_GAP = 12;
const SELECTED_BORDER_COLOR = "#5E3D2D";

/**
 * Horizontal carousel of photo thumbnails.
 * @param {string[]} photos - Array of photo URIs
 * @param {number} [selectedIndex] - Index of the selected thumb (enables selection UI)
 * @param {function(number)} [onSelect] - Called with index when a thumb is pressed (makes carousel selectable)
 * @param {function(number)} [onRemove] - Called with index when remove is pressed (shows remove icon on each thumb)
 * @param {function} [onAddPhoto] - When provided, renders an "Add photos" tile as the first item
 * @param {string} [title] - Optional label above the carousel
 * @param {number} [thumbSize] - Thumbnail width/height (default 76)
 * @param {number} [gap] - Space between thumbnails (default 12)
 * @param {object} [style] - Container style
 * @param {object} [contentContainerStyle] - ScrollView contentContainerStyle
 */
function PhotoCarousel({
  photos,
  selectedIndex,
  onSelect,
  onRemove,
  onAddPhoto,
  title,
  thumbSize = DEFAULT_THUMB_SIZE,
  gap = DEFAULT_GAP,
  highlightIndices,
  highlightColors,
  style,
  contentContainerStyle,
}) {
  const isSelectable = typeof onSelect === "function";
  const showRemove = typeof onRemove === "function";
  const showAddPhoto = typeof onAddPhoto === "function";

  if (!showAddPhoto && (!photos || photos.length === 0)) {
    return null;
  }

  const photoList = photos || [];

  const baseThumbStyle = [
    styles.thumb,
    {
      width: thumbSize,
      height: thumbSize,
      marginRight: gap,
      borderRadius: thumbSize / 6,
    },
  ];
  const selectedStyle = { borderColor: SELECTED_BORDER_COLOR, borderWidth: 3 };
  const defaultHighlightColors = ["#FBBF24", "#8B5CF6", "#60A5FA", "#34D399"];

  const getHighlightStyle = (index) => {
    if (!Array.isArray(highlightIndices) || highlightIndices.length === 0) return null;
    if (!highlightIndices.includes(index)) return null;

    const highlightIdx = highlightIndices.indexOf(index);
    const borderColor =
      (Array.isArray(highlightColors) && highlightColors[highlightIdx]) ||
      defaultHighlightColors[highlightIdx] ||
      defaultHighlightColors[0];

    return { borderColor, borderWidth: 4 };
  };

  const renderThumb = (uri, index) => {
    const isSelected = selectedIndex === index;
    const image = <Image source={{ uri }} style={styles.thumbImage} resizeMode="cover" />;

    const thumbContent = (
      <>
        {image}
        {showRemove && (
          <TouchableOpacity
            style={styles.removeButton}
            onPress={() => onRemove(index)}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          >
            <Text style={styles.removeButtonText}>×</Text>
          </TouchableOpacity>
        )}
      </>
    );

    if (isSelectable) {
      return (
      <TouchableOpacity
        key={`${uri}-${index}`}
        onPress={() => onSelect(index)}
        activeOpacity={0.8}
        style={[
          baseThumbStyle,
          isSelected && selectedStyle,
          getHighlightStyle(index),
        ]}
      >
          {thumbContent}
        </TouchableOpacity>
      );
    }

    return (
    <View
      key={`${uri}-${index}`}
      style={[baseThumbStyle, isSelected && selectedStyle, getHighlightStyle(index)]}
    >
        {thumbContent}
      </View>
    );
  };

  const hasPhotos = photoList.length > 0;
  const cornerRadius = thumbSize / 6;
  const actionIconSize = Math.min(24, thumbSize * 0.28);
  const addPhotoTile = showAddPhoto ? (
    <TouchableOpacity
      key="add-photo"
      style={[
        styles.managePhotoTile,
        {
          width: thumbSize,
          height: thumbSize,
          marginRight: gap,
          borderRadius: cornerRadius,
        },
      ]}
      onPress={onAddPhoto}
      activeOpacity={0.75}
      accessibilityRole="button"
      accessibilityLabel={hasPhotos ? "Manage photos" : "Add photos"}
    >
      {hasPhotos ? (
        <>
          <Ionicons name="images-outline" size={actionIconSize} color="#4d2c1d" />
          <Text style={styles.managePhotoTitle}>Manage</Text>
          <Text style={styles.managePhotoSubtitle}>photos</Text>
        </>
      ) : (
        <>
          <Ionicons name="images-outline" size={actionIconSize} color="#4d2c1d" />
          <Text style={styles.managePhotoTitle}>Add</Text>
          <Text style={styles.managePhotoSubtitle}>photos</Text>
        </>
      )}
    </TouchableOpacity>
  ) : null;

  return (
    <View style={[styles.container, style]}>
      {title ? <Text style={styles.title}>{title}</Text> : null}
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={[styles.scrollContent, contentContainerStyle]}
      >
        {addPhotoTile}
        {photoList.map((uri, index) => renderThumb(uri, index))}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {},
  title: {
    fontSize: 16,
    fontWeight: "600",
    color: "#4d2c1d",
    marginBottom: 8,
  },
  scrollContent: {
    flexDirection: "row",
    alignItems: "center",
  },
  thumb: {
    overflow: "hidden",
    backgroundColor: "#e0e0e0",
    borderWidth: 3,
    borderColor: "transparent",
  },
  thumbImage: {
    width: "100%",
    height: "100%",
  },
  removeButton: {
    position: "absolute",
    top: 4,
    right: 4,
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: "rgba(0,0,0,0.6)",
    justifyContent: "center",
    alignItems: "center",
  },
  removeButtonText: {
    color: "#fff",
    fontSize: 18,
    fontWeight: "700",
    lineHeight: 20,
  },
  /** First tile: “Add” / “photos” (empty) or “Manage” / “photos” (has photos) — same card chrome */
  managePhotoTile: {
    backgroundColor: "#ffffff",
    borderWidth: 1,
    borderColor: "rgba(77, 44, 29, 0.14)",
    alignItems: "center",
    justifyContent: "center",
    shadowColor: "#2d1a12",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 3,
  },
  managePhotoTitle: {
    marginTop: 5,
    fontSize: 11,
    fontWeight: "700",
    color: "#4d2c1d",
    letterSpacing: 0.3,
  },
  managePhotoSubtitle: {
    marginTop: -1,
    fontSize: 9,
    fontWeight: "500",
    color: "#8b7355",
    letterSpacing: 0.4,
    textTransform: "lowercase",
  },
});

export default PhotoCarousel;
